import classnames from 'classnames';

import Breadcrumbs from 'components/HealthcareEntries/Breadcrumbs';
import generateBreadcrumbs from 'components/HealthcareEntries/Breadcrumbs/utils/generateBreadcrumbs';
import Content from 'components/HealthcareEntries/Content';
import Header from 'components/HealthcareEntries/Header';
import styles from 'components/HealthcareEntries/entry.module.scss';

import { replaceFirstPathSegment } from 'utils/functions';
import SendGtmEvent from 'utils/hooks/sendGtmEvent';

import type { BuilderContentProps } from 'components/Builder/BuilderContent';
import type { ComponentPropsWithRef, FC } from 'react';
import type { TemplateModelData } from 'types/types';

type HealthCareEntriesProps = BuilderContentProps & ComponentPropsWithRef<'article'>;

const HealthCareEntries: FC<HealthCareEntriesProps> = ({ components, content, model }) => {
  const templateData = content as TemplateModelData;

  const title = templateData?.data?.templateInfo?.title || null;
  const updateSlugFromLCBuilderValue = replaceFirstPathSegment(content?.data?.url || '', 'healthcare-insights') || '';

  const authors = (
    templateData?.data?.templateInfo?.entryDetails?.authors?.map(({ person }) => person?.value?.data?.fullName) || []
  ).filter((name): name is string => typeof name === 'string');

  const breadcrumbsObject = generateBreadcrumbs(model);

  const publishedDate = content?.data?.templateInfo?.entryDetails?.firstPublished || content?.firstPublished || null;

  const convertedPublishedDate = publishedDate
    ? new Date(publishedDate).toLocaleDateString('en-US', { month: '2-digit', year: '2-digit' })
    : null;

  const generateGtmEvent = () => {
    switch (model) {
      case 'template-article':
      case 'template-claims-lesson':
      case 'template-podcast':
        return (
          <SendGtmEvent
            variant="content"
            eventData={{
              contentId: content?.id,
              contentProviderType: content?.data?.templateInfo?.filters?.profession,
              contentPublishDate: content?.data?.templateInfo?.entryDetails?.firstPublished || content?.firstPublished,
              contentSpecialties: content?.data?.templateInfo?.filters?.specialty,
              contentStatus: 'active',
              contentTags: content?.data?.templateInfo?.filters?.pbrType,
              contentTitle: content?.data?.templateInfo?.title,
              contentType: model,
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {generateGtmEvent()}
      <section>
        <article className={styles.background}>
          {breadcrumbsObject && (
            <div className={styles.breadcrumbs}>
              <Breadcrumbs {...breadcrumbsObject} />
            </div>
          )}
          <div className={classnames(styles.main)}>
            <Header authors={authors} model={model} slug={updateSlugFromLCBuilderValue} title={title} />
            <Content components={components} content={content} model={model} publishedDate={convertedPublishedDate} />
          </div>
          <div className={styles.wrapper} />
        </article>
      </section>
    </>
  );
};

export default HealthCareEntries;
